import { useMemo, useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

const GOLD     = "#C9A96E";
const INK      = "#1C1915";
const INK_SOFT = "#4A4540";
const BORDER   = "#E7E1D8";
const SOFT_BG  = "#F7F5F2";
const WHITE    = "#FFFFFF";

const inputStyle = {
  width: "100%", padding: "9px 11px",
  border: `1px solid ${BORDER}`, borderRadius: 7,
  fontSize: 13, color: INK, background: "#FAFAF8",
  boxSizing: "border-box", outline: "none",
};
const labelStyle = {
  display: "block", fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600,
};
const sectionTitle = {
  fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase",
  color: GOLD, marginBottom: 14, fontWeight: 700,
};
const card = {
  background: WHITE, border: `1px solid ${BORDER}`,
  borderRadius: 12, padding: "22px 24px", marginBottom: 16,
};

export default function StrategicDossierPage({ setActiveTab, cabinetId = null }) {
  const STORAGE_KEY = "yoma_strategic_dossier_page_v1";
  const [prospects, setProspects] = useState([]);
  const [prospectId, setProspectId] = useState("");
  const [nomClient,    setNomClient]    = useState("");
  const [nomProjet,    setNomProjet]    = useState("");
  const [localisation, setLocalisation] = useState("");
  const [typeProjet,   setTypeProjet]   = useState("");
  const [emailClient,  setEmailClient]  = useState("");
  const [faisabiliteTechnique,      setFaisabiliteTechnique]      = useState("");
  const [faisabiliteAdministrative, setFaisabiliteAdministrative] = useState("");
  const [etatFoncier,               setEtatFoncier]               = useState("");
  const [complexite,                setComplexite]                = useState("");
  const [budgetTravauxRealiste,    setBudgetTravauxRealiste]    = useState("");
  const [budgetGlobalCible,        setBudgetGlobalCible]        = useState("");
  const [coherenceBudget,          setCoherenceBudget]          = useState("");
  const [enveloppeAPrevisionnelle, setEnveloppeAPrevisionnelle] = useState("");
  const [risqueAdministratif,        setRisqueAdministratif]        = useState("");
  const [risqueTechnique,            setRisqueTechnique]            = useState("");
  const [risqueFinancier,            setRisqueFinancier]            = useState("");
  const [risqueCalendrier,           setRisqueCalendrier]           = useState("");
  const [intervenants,               setIntervenants]               = useState("");
  const [recommandationConstructeur, setRecommandationConstructeur] = useState("");
  const [notesStrategiques,          setNotesStrategiques]          = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailMsg,     setEmailMsg]     = useState({ text: "", type: "success" });
  const [hydrated,     setHydrated]     = useState(false);

  useEffect(() => {
    if (cabinetId == null) return;
    supabase.from("prospects")
      .select("id, prenom, nom, type_projet, zone_souhaitee, budget_min, budget_max, delai, email")
      .eq("cabinet_id", cabinetId)
      .order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setProspects(data); });
  }, [cabinetId]);

  // Lecture localStorage au montage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setHydrated(true);
        return;
      }
      const parsed = JSON.parse(raw);
      setNomClient(parsed.nomClient || "");
      setNomProjet(parsed.nomProjet || "");
      setLocalisation(parsed.localisation || "");
      setTypeProjet(parsed.typeProjet || "");
      setEmailClient(parsed.emailClient || "");
      setFaisabiliteTechnique(parsed.faisabiliteTechnique || "");
      setFaisabiliteAdministrative(parsed.faisabiliteAdministrative || "");
      setEtatFoncier(parsed.etatFoncier || "");
      setComplexite(parsed.complexite || "");
      setBudgetTravauxRealiste(parsed.budgetTravauxRealiste || "");
      setBudgetGlobalCible(parsed.budgetGlobalCible || "");
      setCoherenceBudget(parsed.coherenceBudget || "");
      setEnveloppeAPrevisionnelle(parsed.enveloppeAPrevisionnelle || "");
      setRisqueAdministratif(parsed.risqueAdministratif || "");
      setRisqueTechnique(parsed.risqueTechnique || "");
      setRisqueFinancier(parsed.risqueFinancier || "");
      setRisqueCalendrier(parsed.risqueCalendrier || "");
      setIntervenants(parsed.intervenants || "");
      setRecommandationConstructeur(parsed.recommandationConstructeur || "");
      setNotesStrategiques(parsed.notesStrategiques || "");
    } catch (_) {
      // ignore erreurs JSON / accès
    }
    setHydrated(true);
  }, []);

  // Écriture localStorage à chaque modification de champs (après hydratation)
  useEffect(() => {
    if (!hydrated) return;
    try {
      const payload = {
        nomClient,
        nomProjet,
        localisation,
        typeProjet,
        emailClient,
        faisabiliteTechnique,
        faisabiliteAdministrative,
        etatFoncier,
        complexite,
        budgetTravauxRealiste,
        budgetGlobalCible,
        coherenceBudget,
        enveloppeAPrevisionnelle,
        risqueAdministratif,
        risqueTechnique,
        risqueFinancier,
        risqueCalendrier,
        intervenants,
        recommandationConstructeur,
        notesStrategiques,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) {
      // ignore erreurs d'écriture
    }
  }, [
    hydrated,
    nomClient,
    nomProjet,
    localisation,
    typeProjet,
    emailClient,
    faisabiliteTechnique,
    faisabiliteAdministrative,
    etatFoncier,
    complexite,
    budgetTravauxRealiste,
    budgetGlobalCible,
    coherenceBudget,
    enveloppeAPrevisionnelle,
    risqueAdministratif,
    risqueTechnique,
    risqueFinancier,
    risqueCalendrier,
    intervenants,
    recommandationConstructeur,
    notesStrategiques,
  ]);

  function handleSelectProspect(id) {
    setProspectId(id);
    if (!id) return;
    const p = prospects.find(p => p.id === id);
    if (!p) return;
    setNomClient(`${p.prenom} ${p.nom}`);
    setTypeProjet(p.type_projet || "");
    setLocalisation(p.zone_souhaitee || "");
    if (p.budget_max) setBudgetGlobalCible(String(p.budget_max));
    if (p.budget_min) setBudgetTravauxRealiste(String(p.budget_min));
    if (p.email)      setEmailClient(p.email);
  }

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }

  async function handleSendEmail(type) {
    if (!emailClient) {
      setEmailMsg({ text: "Veuillez saisir l'email du client.", type: "error" });
      return;
    }
    setSendingEmail(true);
    setEmailMsg({ text: "", type: "success" });
    const nomComplet = nomClient || "Monsieur/Madame";
    const configs = {
      complement: {
        subject: `YOMA DESIGN — Votre projet ${typeProjet || ""} — Informations complémentaires`,
        body: `Bonjour ${nomComplet},\n\nNous avons bien réceptionné votre dossier concernant votre projet${typeProjet ? ` de ${typeProjet}` : ""}${localisation ? ` à ${localisation}` : ""}.\n\nAprès une première analyse, votre projet suscite notre intérêt. Cependant, afin de finaliser notre étude de faisabilité et vous proposer une offre adaptée, nous aurions besoin des éléments complémentaires suivants :\n\n- Confirmation du budget global disponible\n- Documents relatifs au foncier (titre ou situation en cours)\n- Précisions sur le calendrier souhaité\n\nNous restons à votre disposition pour tout échange.\n\nCordialement,\n\nL'équipe YOMA DESIGN\ncontact@yoma.design`,
      },
      decline: {
        subject: `YOMA DESIGN — Votre projet ${typeProjet || ""} — Suite donnée`,
        body: `Bonjour ${nomComplet},\n\nNous vous remercions de la confiance que vous nous accordez.\n\nAprès analyse approfondie, nous sommes au regret de ne pas pouvoir donner suite à votre demande dans le cadre de notre offre actuelle.\n\nNous vous souhaitons plein succès dans la réalisation de votre projet.\n\nCordialement,\n\nL'équipe YOMA DESIGN\ncontact@yoma.design`,
      },
    };
    const { subject, body } = configs[type];
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ to: emailClient, subject, body }),
        }
      );
      const result = await res.json();
      if (result.success) {
        setEmailMsg({ text: `✓ Email envoyé à ${emailClient}`, type: "success" });
      } else {
        setEmailMsg({ text: "Erreur : " + (result.error || "inconnue"), type: "error" });
      }
    } catch (err) {
      setEmailMsg({ text: "Erreur réseau : " + err.message, type: "error" });
    }
    setSendingEmail(false);
  }

  const analyse = useMemo(() => {
    let score = 0;
    const reserves = [];
    const alertes = [];
    if (faisabiliteTechnique === "favorable")        score += 2;
    if (faisabiliteTechnique === "reserve")          { score += 1; reserves.push("Faisabilité technique partielle ou sous réserve."); }
    if (faisabiliteTechnique === "defavorable")      alertes.push("Faisabilité technique défavorable.");
    if (faisabiliteAdministrative === "favorable")   score += 2;
    if (faisabiliteAdministrative === "reserve")     { score += 1; reserves.push("Faisabilité administrative sous réserve."); }
    if (faisabiliteAdministrative === "defavorable") alertes.push("Faisabilité administrative défavorable.");
    if (etatFoncier === "titre")                     score += 2;
    if (etatFoncier === "acquisition-serieuse")      score += 1;
    if (etatFoncier === "melkia")                    reserves.push("Foncier en melkia : vérifications renforcées requises.");
    if (etatFoncier === "incertain")                 alertes.push("Foncier incertain ou non sécurisé.");
    if (complexite === "faible")  score += 2;
    if (complexite === "moyenne") score += 1;
    if (complexite === "forte")   reserves.push("Projet à forte complexité opérationnelle.");
    if (coherenceBudget === "coherent")   score += 2;
    if (coherenceBudget === "partiel")    { score += 1; reserves.push("Budget partiellement cohérent."); }
    if (coherenceBudget === "incoherent") alertes.push("Incohérence budgétaire majeure.");
    if (risqueAdministratif === "faible") score += 1;
    if (risqueAdministratif === "moyen")  reserves.push("Risque administratif à surveiller.");
    if (risqueAdministratif === "eleve")  alertes.push("Risque administratif élevé.");
    if (risqueTechnique === "faible") score += 1;
    if (risqueTechnique === "moyen")  reserves.push("Risque technique à surveiller.");
    if (risqueTechnique === "eleve")  alertes.push("Risque technique élevé.");
    if (risqueFinancier === "faible") score += 1;
    if (risqueFinancier === "moyen")  reserves.push("Risque financier à surveiller.");
    if (risqueFinancier === "eleve")  alertes.push("Risque financier élevé.");
    if (risqueCalendrier === "faible") score += 1;
    if (risqueCalendrier === "moyen")  reserves.push("Risque calendrier à surveiller.");
    if (risqueCalendrier === "eleve")  alertes.push("Risque calendrier élevé.");
    let decision = "Dossier favorable";
    let niveau   = "ok";
    if (alertes.length >= 2 || faisabiliteTechnique === "defavorable" || faisabiliteAdministrative === "defavorable" || coherenceBudget === "incoherent") {
      decision = "Dossier défavorable"; niveau = "danger";
    } else if (reserves.length > 0 || alertes.length === 1) {
      decision = "Dossier favorable avec réserves"; niveau = "warning";
    }
    const action = decision === "Dossier défavorable"
      ? "Ne pas émettre de proposition contractuelle tant que les points bloquants ne sont pas levés."
      : decision === "Dossier favorable avec réserves"
      ? "Compléter les réserves, verrouiller les points critiques, puis préparer la proposition."
      : "Passer à l'étape 3 : proposition contractuelle interne.";
    return { score, reserves, alertes, decision, niveau, action };
  }, [faisabiliteTechnique, faisabiliteAdministrative, etatFoncier, complexite, coherenceBudget, risqueAdministratif, risqueTechnique, risqueFinancier, risqueCalendrier]);

  const badgeStyle = analyse.niveau === "ok"
    ? { bg: "#E8F6EF", color: "#1A7A44", border: "#B7E1C1" }
    : analyse.niveau === "warning"
    ? { bg: "#FEF9EE", color: "#B7770D", border: "#F0DEB4" }
    : { bg: "#FDEDEC", color: "#C0392B", border: "#F2C4C0" };

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: INK_SOFT }}>Chargement...</div>;
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1100 }}><div style={{ marginBottom: 24 }}><div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: GOLD, marginBottom: 4 }}>Décision — Étape 2</div><div style={{ fontSize: 24, fontWeight: 700 }}>Dossier Stratégique</div><div style={{ fontSize: 13, color: INK_SOFT, marginTop: 3 }}>Analyse structurée du projet avant toute proposition contractuelle</div></div><div style={{ ...card, background: `linear-gradient(135deg, ${GOLD}15, ${SOFT_BG})`, border: `1px solid ${GOLD}44`, marginBottom: 20 }}><div style={sectionTitle}> Charger un prospect existant</div><div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "end" }}><div><label style={labelStyle}>Sélectionner un prospect</label><select style={inputStyle} value={prospectId} onChange={e => handleSelectProspect(e.target.value)}><option value="">— Choisir un prospect —</option>
              {prospects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.prenom} {p.nom} {p.type_projet ? `· ${p.type_projet}` : ""} {p.zone_souhaitee ? `· ${p.zone_souhaitee}` : ""}
                </option>
              ))}
            </select></div>
          {prospectId && (
            <button onClick={() => { setProspectId(""); setNomClient(""); setNomProjet(""); setLocalisation(""); setTypeProjet(""); setBudgetTravauxRealiste(""); setBudgetGlobalCible(""); setEmailClient(""); }}
              style={{ padding: "9px 16px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 7, fontSize: 12, cursor: "pointer" }}>
              ✕ Effacer
            </button>
          )}
        </div>
        {prospectId && (
          <div style={{ marginTop: 10, fontSize: 12, color: "#1A7A44", fontWeight: 600 }}>✓ Données pré-remplies depuis la fiche prospect</div>
        )}
      </div><div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 20, alignItems: "start" }}><div><div style={card}><div style={sectionTitle}>Informations générales</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={labelStyle}>Nom du client</label><input style={inputStyle} value={nomClient} onChange={e => setNomClient(e.target.value)} placeholder="Ex : M. Hassan El Amrani" /></div><div><label style={labelStyle}>Nom du projet</label><input style={inputStyle} value={nomProjet} onChange={e => setNomProjet(e.target.value)} placeholder="Villa Al Karam..." /></div><div><label style={labelStyle}>Localisation</label><input style={inputStyle} value={localisation} onChange={e => setLocalisation(e.target.value)} placeholder="Zone, quartier..." /></div><div><label style={labelStyle}>Type de projet</label><select style={inputStyle} value={typeProjet} onChange={e => setTypeProjet(e.target.value)}><option value="">— Sélectionner —</option>
                  {typeProjet && !["Villa neuve", "Lotissement", "Immeuble", "Rénovation", "Terrain + construction", "Riad"].includes(typeProjet) && (
                    <option value={typeProjet}>{typeProjet}</option>
                  )}
                  <option value="Villa neuve">Villa neuve</option><option value="Lotissement">Lotissement</option><option value="Immeuble">Immeuble</option><option value="Rénovation">Rénovation</option><option value="Terrain + construction">Terrain + construction</option><option value="Riad">Riad</option></select></div><div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Email du client</label><input style={inputStyle} type="email" value={emailClient} onChange={e => setEmailClient(e.target.value)} placeholder="client@email.com" /></div></div></div><div style={card}><div style={sectionTitle}>Faisabilité</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={labelStyle}>Faisabilité technique</label><select style={inputStyle} value={faisabiliteTechnique} onChange={e => setFaisabiliteTechnique(e.target.value)}><option value="">— Sélectionner —</option><option value="favorable">Favorable</option><option value="reserve">Sous réserve</option><option value="defavorable">Défavorable</option></select></div><div><label style={labelStyle}>Faisabilité administrative</label><select style={inputStyle} value={faisabiliteAdministrative} onChange={e => setFaisabiliteAdministrative(e.target.value)}><option value="">— Sélectionner —</option><option value="favorable">Favorable</option><option value="reserve">Sous réserve</option><option value="defavorable">Défavorable</option></select></div><div><label style={labelStyle}>État du foncier</label><select style={inputStyle} value={etatFoncier} onChange={e => setEtatFoncier(e.target.value)}><option value="">— Sélectionner —</option><option value="titre">Titré</option><option value="acquisition-serieuse">Acquisition sérieuse en cours</option><option value="melkia">Melkia</option><option value="incertain">Incertain / non sécurisé</option></select></div><div><label style={labelStyle}>Niveau de complexité</label><select style={inputStyle} value={complexite} onChange={e => setComplexite(e.target.value)}><option value="">— Sélectionner —</option><option value="faible">Faible</option><option value="moyenne">Moyenne</option><option value="forte">Forte</option></select></div></div></div><div style={card}><div style={sectionTitle}>Budget & structure</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={labelStyle}>Budget travaux réaliste (MAD)</label><input style={inputStyle} type="number" value={budgetTravauxRealiste} onChange={e => setBudgetTravauxRealiste(e.target.value)} placeholder="Ex : 1 800 000" /></div><div><label style={labelStyle}>Budget global cible (MAD)</label><input style={inputStyle} type="number" value={budgetGlobalCible} onChange={e => setBudgetGlobalCible(e.target.value)} placeholder="Ex : 2 500 000" /></div><div><label style={labelStyle}>Cohérence budgétaire</label><select style={inputStyle} value={coherenceBudget} onChange={e => setCoherenceBudget(e.target.value)}><option value="">— Sélectionner —</option><option value="coherent">Cohérent</option><option value="partiel">Partiellement cohérent</option><option value="incoherent">Incohérent</option></select></div><div><label style={labelStyle}>Enveloppe A prévisionnelle (MAD)</label><input style={inputStyle} type="number" value={enveloppeAPrevisionnelle} onChange={e => setEnveloppeAPrevisionnelle(e.target.value)} placeholder="Ex : 150 000" /></div></div></div><div style={card}><div style={sectionTitle}>Risques & intervenants</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              {[
                { label: "Risque administratif", val: risqueAdministratif, set: setRisqueAdministratif },
                { label: "Risque technique",      val: risqueTechnique,     set: setRisqueTechnique },
                { label: "Risque financier",      val: risqueFinancier,     set: setRisqueFinancier },
                { label: "Risque calendrier",     val: risqueCalendrier,    set: setRisqueCalendrier },
              ].map(r => (
                <div key={r.label}><label style={labelStyle}>{r.label}</label><select style={inputStyle} value={r.val} onChange={e => r.set(e.target.value)}><option value="">— Sélectionner —</option><option value="faible">Faible</option><option value="moyen">Moyen</option><option value="eleve">Élevé</option></select></div>
              ))}
            </div><div style={{ marginBottom: 12 }}><label style={labelStyle}>Intervenants nécessaires</label><textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={intervenants} onChange={e => setIntervenants(e.target.value)} placeholder="Architecte, géomètre, bureau d'études..." /></div><div style={{ marginBottom: 12 }}><label style={labelStyle}>Recommandation constructeur</label><textarea style={{ ...inputStyle, minHeight: 80, resize: "vertical" }} value={recommandationConstructeur} onChange={e => setRecommandationConstructeur(e.target.value)} placeholder="Corps d'état recommandés..." /></div><div><label style={labelStyle}>Notes stratégiques</label><textarea style={{ ...inputStyle, minHeight: 100, resize: "vertical" }} value={notesStrategiques} onChange={e => setNotesStrategiques(e.target.value)} placeholder="Observations, points d'attention, contexte..." /></div></div></div><div><div style={{ ...card, borderTop: `4px solid ${badgeStyle.color}` }}><div style={sectionTitle}>Décision YOMA</div><div style={{ background: badgeStyle.bg, border: `1px solid ${badgeStyle.border}`, borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}><div style={{ fontSize: 15, fontWeight: 800, color: badgeStyle.color }}>{analyse.decision || "Remplissez le formulaire"}</div></div><div style={{ marginBottom: 12 }}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: INK_SOFT, marginBottom: 4 }}>Score dossier</div><div style={{ fontSize: 22, fontWeight: 800, color: badgeStyle.color }}>{analyse.score} pts</div></div><div style={{ background: SOFT_BG, borderRadius: 8, padding: "12px 14px", borderLeft: `3px solid ${badgeStyle.color}`, marginBottom: 16 }}><div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: badgeStyle.color, marginBottom: 6 }}>Action suivante</div><div style={{ fontSize: 13, color: INK, lineHeight: 1.6 }}>{analyse.action}</div></div>
            {analyse.alertes.length > 0 && (
              <div style={{ marginBottom: 12 }}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#C0392B", marginBottom: 8 }}> Alertes majeures</div>
                {analyse.alertes.map((a, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#C0392B", background: "#FDEDEC", border: "1px solid #F2C4C0", borderRadius: 6, padding: "7px 10px", marginBottom: 6 }}> {a}</div>
                ))}
              </div>
            )}
            {analyse.reserves.length > 0 && (
              <div style={{ marginBottom: 12 }}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#B7770D", marginBottom: 8 }}> Réserves</div>
                {analyse.reserves.map((r, i) => (
                  <div key={i} style={{ fontSize: 12, color: "#B7770D", background: "#FEF9EE", border: "1px solid #F0DEB4", borderRadius: 6, padding: "7px 10px", marginBottom: 6 }}>› {r}</div>
                ))}
              </div>
            )}
          </div><div style={card}><div style={sectionTitle}>Lecture économique</div>
            {[
              { label: "Budget travaux réaliste",    val: budgetTravauxRealiste },
              { label: "Budget global cible",        val: budgetGlobalCible },
              { label: "Enveloppe A prévisionnelle", val: enveloppeAPrevisionnelle },
            ].map(r => (
              <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${BORDER}` }}><span style={{ fontSize: 12, color: INK_SOFT }}>{r.label}</span><span style={{ fontSize: 13, fontWeight: 700, color: GOLD }}>{formatMoney(r.val)}</span></div>
            ))}
            <div style={{ marginTop: 14, padding: "12px 14px", background: SOFT_BG, borderRadius: 8, borderLeft: `3px solid ${GOLD}`, fontSize: 12, color: INK_SOFT, lineHeight: 1.6 }}>
              Le dossier stratégique est un livrable écrit. Il sert à décider si YOMA émet ou non une proposition contractuelle.
            </div></div>

          {faisabiliteTechnique && faisabiliteAdministrative && (
            <div style={{ ...card, borderTop: `3px solid ${badgeStyle.color}` }}><div style={sectionTitle}>Actions</div>

              {emailMsg.text && (
                <div style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: emailMsg.type === "error" ? "#FDEDEC" : "#E8F6EF", color: emailMsg.type === "error" ? "#C0392B" : "#1A7A44" }}>
                  {emailMsg.text}
                </div>
              )}

              {analyse.niveau === "ok" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}><div style={{ fontSize: 12, color: INK_SOFT, lineHeight: 1.6 }}>
                    Le dossier est favorable. Vous pouvez passer à la proposition contractuelle.
                  </div><button
                    onClick={() => {
                      if (typeof setActiveTab === "function") {
                        setActiveTab("proposition");
                      } else {
                        alert("Erreur : setActiveTab non disponible");
                      }
                    }}
                    style={{ padding: "12px 20px", background: "#1A7A44", color: WHITE, border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                    → Lancer la proposition contractuelle
                  </button></div>
              )}

              {analyse.niveau === "warning" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}><div style={{ fontSize: 12, color: INK_SOFT, lineHeight: 1.6 }}>
                    Des réserves subsistent. Envoyez un email au client pour obtenir les informations manquantes.
                  </div><button
                    onClick={() => handleSendEmail("complement")}
                    disabled={sendingEmail}
                    style={{ padding: "12px 20px", background: "#B7770D", color: WHITE, border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: sendingEmail ? "not-allowed" : "pointer", opacity: sendingEmail ? 0.6 : 1 }}>
                    {sendingEmail ? "Envoi en cours..." : " Demander les informations complémentaires"}
                  </button></div>
              )}

              {analyse.niveau === "danger" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}><div style={{ fontSize: 12, color: INK_SOFT, lineHeight: 1.6 }}>
                    Le dossier est défavorable. Envoyez un email au client pour lui notifier que vous ne donnez pas suite.
                  </div><button
                    onClick={() => handleSendEmail("decline")}
                    disabled={sendingEmail}
                    style={{ padding: "12px 20px", background: "#C0392B", color: WHITE, border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: sendingEmail ? "not-allowed" : "pointer", opacity: sendingEmail ? 0.6 : 1 }}>
                    {sendingEmail ? "Envoi en cours..." : " Notifier le client du déclin"}
                  </button></div>
              )}
            </div>
          )}
        </div></div></div>
  );
}
